(() => {
  const selector = '[data-winter-catalog]';
  const instances = new WeakMap();

  // Use Autumn's controls, timing, looping and swipe behavior. Winter's
  // requested crossfade replaces Autumn's horizontal rail transition. Photo
  // selectors are a Winter-only addition requested during preview review.
  class WinterCatalogSlider {
    constructor(element, { autoplay, delay, signal }) {
      this.element = element;
      this.slides = [...element.querySelectorAll('.winter-slider__slide')];
      this.dots = [...element.querySelectorAll('.winter-slider__dot')];
      this.toggle = element.querySelector('.winter-slider__toggle');
      this.delay = delay;
      this.autoplayEnabled = autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.index = 0;
      this.timer = null;
      this.isPaused = !this.autoplayEnabled;
      this.pointerStart = null;
      this.wasDragged = false;
      this.signal = signal;
      if (this.slides.length < 2 || !this.toggle) return;

      const listen = (name, handler) => element.addEventListener(name, handler, { signal });
      this.toggle.addEventListener('click', () => {
        this.isPaused = !this.isPaused;
        if (this.isPaused) this.stop();
        else this.start();
        this.updateToggle();
      }, { signal });
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.stop();
          this.update(index);
        }, { signal });
      });

      listen('pointerdown', event => {
        if (event.target.closest('button')) {
          this.wasDragged = false;
          return;
        }
        this.pointerStart = event.clientX;
        this.wasDragged = false;
        this.stop();
      });
      listen('pointermove', event => {
        if (this.pointerStart === null) return;
        if (Math.abs(event.clientX - this.pointerStart) > 10) this.wasDragged = true;
      });
      listen('pointerup', event => {
        if (this.pointerStart === null) return;
        const distance = event.clientX - this.pointerStart;
        this.pointerStart = null;
        if (Math.abs(distance) > 40) this.update(this.index + (distance < 0 ? 1 : -1));
        if (!this.isPaused) this.start();
      });
      listen('pointercancel', () => {
        this.pointerStart = null;
        if (!this.isPaused) this.start();
      });
      // A swipe changes the photograph, without following its product link.
      element.addEventListener('click', event => {
        if (!this.wasDragged) return;
        event.preventDefault();
        this.wasDragged = false;
      }, { signal, capture: true });
      listen('mouseenter', () => this.stop());
      listen('mouseleave', () => {
        this.pointerStart = null;
        if (!this.isPaused) this.start();
      });
      listen('focusin', () => this.stop());
      listen('focusout', () => {
        if (!this.isPaused) this.start();
      });

      element.classList.add('is-initialized');
      this.update(0);
      this.updateToggle();
      if (this.autoplayEnabled) this.start();
    }

    update(next) {
      this.index = (next + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', String(active));
      });
    }

    updateToggle() {
      this.toggle.classList.toggle('is-paused', this.isPaused);
      this.toggle.setAttribute('aria-pressed', String(this.isPaused));
      this.toggle.setAttribute('aria-label', this.isPaused ? 'スライドショーを再生' : 'スライドショーを一時停止');
    }

    start() {
      if (!this.autoplayEnabled || this.signal.aborted) return;
      this.stop();
      this.timer = window.setInterval(() => this.update(this.index + 1), this.delay);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  function initialize(root) {
    if (!root || instances.has(root)) return;
    const abort = new AbortController();
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoplay = root.dataset.autoplay === 'true';
    const delay = Number.parseInt(root.dataset.autoplayDelay, 10) || 3000;
    const sliders = [...root.querySelectorAll('[data-winter-slider]')].map(element => new WinterCatalogSlider(element, { autoplay, delay, signal: abort.signal }));
    const scenes = [...root.querySelectorAll('[data-winter-reveal]')];
    let observer;
    if ('IntersectionObserver' in window && !motion.matches) {
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
      scenes.forEach(scene => observer.observe(scene));
      root.classList.add('is-motion-ready');
    } else {
      scenes.forEach(scene => scene.classList.add('is-visible'));
    }
    motion.addEventListener('change', () => {
      if (motion.matches) scenes.forEach(scene => scene.classList.add('is-visible'));
      for (const slider of sliders) {
        slider.autoplayEnabled = autoplay && !motion.matches;
        if (motion.matches) {
          slider.isPaused = true;
          slider.stop();
          slider.updateToggle();
        }
      }
    }, { signal: abort.signal });
    instances.set(root, () => {
      abort.abort();
      observer?.disconnect();
      sliders.forEach(slider => slider.stop());
      root.classList.remove('is-motion-ready');
    });
  }

  document.querySelectorAll(selector).forEach(initialize);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll(selector).forEach(initialize));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(selector).forEach(root => { instances.get(root)?.(); instances.delete(root); });
  });
})();
